/// <reference types="../../types/modules/tiny-inflate.d.ts" />
import inflate from 'tiny-inflate';

/**
 * Parses a font file and returns all supported Unicode character indices. Yes,
 * this handles binary data. If you want to debug this, beware of dragons and
 * have the OpenType spec ready:
 * https://docs.microsoft.com/en-us/typography/opentype/spec/cmap
 * @param buffer an ArrayBuffer containing the font data.
 */
export function getSupportedCharactersOfFont(buffer: ArrayBuffer) {
  const glyphIndices = glyphIndicesFromBuffer(buffer);
  return glyphIndices.reduce<string[]>((a, c) => {
    for (let i = c[0]; i <= c[1]; i++) {
      a.push(String.fromCharCode(i));
    }
    return a;
  }, []);
}

interface TableEntry {
  length: number;
  offset: number;
  compression: string | boolean;
  compressedLength?: number;
}

function getOpenTypeCmap(data: DataView): TableEntry | null {
  const numTables = data.getUint16(12, false);
  let p = 12;
  for (let i = 0; i < numTables; i++) {
    const tag = getTag(data, p);
    const entry = {
      compression: false,
      length: data.getUint32(p + 12),
      offset: data.getUint32(p + 8),
    };
    if (tag === 'cmap') {
      return entry;
    }
    p += 16;
  }
  return null;
}

function getWOFFCmap(data: DataView): TableEntry | null {
  const numTables = data.getUint16(12, false);
  let p = 44;
  for (let i = 0; i < numTables; i += 1) {
    const tag = getTag(data, p);
    const offset = data.getUint32(p + 4);
    const compLength = data.getUint32(p + 8);
    const origLength = data.getUint32(p + 12);
    let compression;
    if (compLength < origLength) {
      compression = 'WOFF';
    } else {
      compression = false;
    }
    const entry = {
      compressedLength: compLength,
      compression,
      length: origLength,
      offset,
    };
    if (tag === 'cmap') {
      return entry;
    }
    p += 20;
  }
  return null;
}

function uncompressTable(data: DataView, tableEntry: TableEntry) {
  if (tableEntry.compression === 'WOFF') {
    const inBuffer = new Uint8Array(
      data.buffer,
      tableEntry.offset + 2,
      tableEntry.compressedLength! - 2
    );
    const outBuffer = new Uint8Array(tableEntry.length);
    inflate(inBuffer, outBuffer);
    if (outBuffer.byteLength !== tableEntry.length) {
      throw new Error("Decompression error: decompressed length doesn't match recorded length");
    }
    const view = new DataView(outBuffer.buffer, 0);
    return { data: view, offset: 0 };
  } else {
    return { data: data, offset: tableEntry.offset };
  }
}

function glyphIndicesFromBuffer(buf: ArrayBuffer): number[][] {
  const data = new DataView(buf, 0);
  const signature = getTag(data, 0);
  let cmapEntry: TableEntry | null;
  if (
    signature === String.fromCharCode(0, 1, 0, 0) ||
    signature === 'true' ||
    signature === 'typ1' ||
    signature === 'OTTO'
  ) {
    cmapEntry = getOpenTypeCmap(data);
  } else if (signature === 'wOFF') {
    cmapEntry = getWOFFCmap(data);
  } else {
    throw new Error('Unsupported OpenType signature' + signature);
  }
  if (cmapEntry == null) {
    throw new Error('Unsupported Font');
  }
  const uncompressedCmap = uncompressTable(data, cmapEntry);
  return glyphIndicesFromCmapTable(uncompressedCmap.data, uncompressedCmap.offset);
}

function getTag(data: DataView, offset: number) {
  let tag = '';
  for (let i = offset; i < offset + 4; i++) {
    tag += String.fromCharCode(data.getInt8(i));
  }
  return tag;
}

class Parser {
  public offset: number;
  public relativeOffset: number;
  private data: DataView;

  constructor(data: DataView, offset: number) {
    this.data = data;
    this.offset = offset;
    this.relativeOffset = 0;
  }

  public parseUShort() {
    const v = this.data.getUint16(this.offset + this.relativeOffset);
    this.relativeOffset += 2;
    return v;
  }

  public parseULong() {
    const v = this.data.getUint32(this.offset + this.relativeOffset);
    this.relativeOffset += 4;
    return v;
  }

  public skip(bytes: number) {
    this.relativeOffset += bytes;
  }
}

function glyphIndicesFromCmapTable(data: DataView, start: number) {
  const numTables = data.getUint16(start + 2);
  let offset = -1;
  for (let i = numTables - 1; i >= 0; i -= 1) {
    const platformId = data.getUint16(start + 4 + i * 8);
    const encodingId = data.getUint16(start + 4 + i * 8 + 2);
    if (
      (platformId === 3 && (encodingId === 0 || encodingId === 1 || encodingId === 10)) ||
      (platformId === 0 &&
        (encodingId === 0 ||
          encodingId === 1 ||
          encodingId === 2 ||
          encodingId === 3 ||
          encodingId === 4))
    ) {
      offset = data.getUint32(start + 4 + i * 8 + 4);
      break;
    }
  }
  if (offset === -1) {
    // There is no cmap table in the font that we support.
    throw new Error('No valid cmap sub-tables found.');
  }
  const parser = new Parser(data, start + offset);
  const format = parser.parseUShort();
  if (format === 12) {
    return glyphIndicesCmapTableFormat12(parser);
  } else if (format === 4) {
    return glyphIndicesCmapTableFormat4(parser, data, start, offset);
  } else {
    throw new Error(
      'Only format 4 and 12 cmap tables are supported (found format ' + format + ').'
    );
  }
}

function glyphIndicesCmapTableFormat12(p: Parser) {
  // skip reserved, length and language
  p.skip(10);
  const groupCount = p.parseULong();
  const glyphIndexArray = [];
  for (let i = 0; i < groupCount; i += 1) {
    const startCharCode = p.parseULong();
    const endCharCode = p.parseULong();
    // skip startGlyphID
    p.skip(4);
    glyphIndexArray.push([startCharCode, endCharCode]);
  }
  return glyphIndexArray;
}

function glyphIndicesCmapTableFormat4(p: Parser, data: DataView, start: number, offset: number) {
  // skip length and language fields
  p.skip(4);
  // segCount is stored x 2.
  const segCount = p.parseUShort() >> 1;
  // Skip searchRange, entrySelector, rangeShift.
  p.skip(6);

  const glyphIndexArray = [];
  const endCountParser = new Parser(data, start + offset + 14);
  const startCountParser = new Parser(data, start + offset + 16 + segCount * 2);
  for (let i = 0; i < segCount - 1; i += 1) {
    const endCount = endCountParser.parseUShort();
    const startCount = startCountParser.parseUShort();
    glyphIndexArray.push([startCount, endCount]);
  }
  return glyphIndexArray;
}
